/**
 * ============================================================================
 * useCapasQuery — list query hook for the CAPA workbench (Phase 7).
 * Owner: Phase 7 — CAPA Module agent.
 * URS rows: CAPA-001..020.
 * ============================================================================ */

import { useEffect, useState, useCallback } from 'react';
import { buildHeaders, apiUrl } from '../../api/client';

export type CapaStatus =
  | 'IDENTIFIED'
  | 'INVESTIGATION'
  | 'ACTION_PLAN'
  | 'IMPLEMENTATION'
  | 'EFFECTIVENESS_CHECK'
  | 'CLOSED'
  | 'CANCELLED';

export type CapaCriticality = 'CRITICAL' | 'MAJOR' | 'MINOR';

export type CapaType = 'CORRECTIVE' | 'PREVENTIVE' | 'BOTH';

export type CapaSourceKind =
  | 'DEVIATION'
  | 'OOS'
  | 'AUDIT_FINDING'
  | 'RISK'
  | 'COMPLAINT'
  | 'MGMT_REVIEW'
  | 'CC_OUTCOME'
  | 'OTHER';

export interface CapaSummary {
  capaId: string;
  capaNumber: string;
  title: string;
  capaType: CapaType;
  sourceKind: CapaSourceKind;
  sourceRef: string | null;
  ownerUserId: string;
  criticality: CapaCriticality;
  status: CapaStatus;
  dueDateUtc: string;
  createdAtUtc: string;
  hasEffectivenessVerification: boolean;
}

export interface CapaWorkbenchStats {
  totalOpen: number;
  criticalOpen: number;
  overdue: number;
  closedEffectiveThisMonth: number;
}

export interface CapaQueryParams {
  status?: CapaStatus;
  criticality?: CapaCriticality;
  sourceKind?: CapaSourceKind;
  ownerUserId?: string;
}

export interface CapaQueryResponse {
  items: CapaSummary[];
  total: number;
  stats?: CapaWorkbenchStats;
}

export interface UseCapasQueryResult {
  data: CapaQueryResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | undefined;
  refetch: () => void;
}

function buildQuery(params: CapaQueryParams): string {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.criticality) search.set('criticality', params.criticality);
  if (params.sourceKind) search.set('sourceKind', params.sourceKind);
  if (params.ownerUserId) search.set('ownerUserId', params.ownerUserId);
  const q = search.toString();
  return q ? `?${q}` : '';
}

export function useCapasQuery(params: CapaQueryParams): UseCapasQueryResult {
  const [data, setData] = useState<CapaQueryResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error>();

  const fetcher = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    setError(undefined);
    try {
      const r = await fetch(apiUrl(`/api/v1/capa${buildQuery(params)}`), {
        headers: buildHeaders({ json: true }),
      });
      if (!r.ok) throw new Error(`CAPA list ${r.status}`);
      const body = (await r.json()) as CapaQueryResponse;
      setData(body);
    } catch (e) {
      setIsError(true);
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setIsLoading(false);
    }
  }, [params.status, params.criticality, params.sourceKind, params.ownerUserId]);

  useEffect(() => {
    fetcher();
  }, [fetcher]);

  return { data, isLoading, isError, error, refetch: fetcher };
}
